import { AnalyticsUserEvent } from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useSetAtom } from 'jotai';
import { useState } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    BackButton,
    BackButtonIconType,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    H6Regular,
    isNonMobileWeb,
    isWeb,
    Spinner,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import { captureException, captureWarning, track } from '../../logger/Logger';
import {
    getGuideBookingLinkUrl,
    getValidSessionBookingDoneRoute,
} from '../helpers';
import { useAfterBookingGuidanceSession } from '../hooks/useAfterBookingGuidanceSession';
import { useGetNextGuidanceOnboardingSessionType } from '../hooks/useGetNextGuidanceSession';
import { useMembershipGuidanceSessionInfo } from '../hooks/useMembershipGuidanceSessionInfo';
import { hasRecentlyBookedSessionAtom } from '../state/GuidanceSessionsAtoms';
import type { GuidanceSessionType } from '../types/GuidanceSessions';
import InlineCalendlyWidget from './InlineCalendlyWidget';

const GUIDANCE_TAB_ROUTE = '/home/my-guide';
const HEADER_SIDE_WIDTH = 40;
const HEADER_SIDE_HEIGHT = 40;
const HEADER_COPY_BASE_HEIGHT = 65;
const DONE_BUTTON_BASE_HEIGHT = 70;

const BookSession = () => {
    const { trackViewOnce } = useViewEvents();
    const user = useAuthedUser();
    const guide = user.membership?.guide;
    const { guidanceOnboardingOrder } = useMembershipGuidanceSessionInfo();
    const { height } = useWindowDimensions();
    const insets = useSafeAreaInsets();

    const queryClient = useQueryClient();
    const { afterBookingGuidanceSession } = useAfterBookingGuidanceSession();

    const [isWaiting, setIsWaiting] = useState(false);
    const [shouldShowDoneButton, setShouldShowDoneButton] = useState(false);
    const shouldShowSimpleHeader = shouldShowDoneButton;

    const setHasRecentlyBookedSession = useSetAtom(
        hasRecentlyBookedSessionAtom,
    );

    const {
        nextGuidanceOnboardingSessionType,
        isLoading: isLoadingNextGuidanceSessionType,
        hasCompletedGuidanceOnboarding,
    } = useGetNextGuidanceOnboardingSessionType();
    const { sessionType, doneRoute } = useLocalSearchParams();
    const bookingGuidanceSessionType =
        (sessionType as GuidanceSessionType | undefined) ??
        nextGuidanceOnboardingSessionType;

    const validDoneRoute = getValidSessionBookingDoneRoute(
        doneRoute as string | undefined,
    );

    if (isLoadingNextGuidanceSessionType) {
        return <FullScreenSpinner />;
    }

    if (bookingGuidanceSessionType == null) {
        captureWarning('No booking guidance session type found');
        return <Redirect href={validDoneRoute} />;
    }

    if (
        !hasCompletedGuidanceOnboarding &&
        bookingGuidanceSessionType !== nextGuidanceOnboardingSessionType
    ) {
        captureWarning(
            `Booking guidance session type '${bookingGuidanceSessionType}' is not the next guidance onboarding session type '${nextGuidanceOnboardingSessionType}'`,
        );
        return <Redirect href={validDoneRoute} />;
    }

    if (
        hasCompletedGuidanceOnboarding &&
        guidanceOnboardingOrder.includes(bookingGuidanceSessionType)
    ) {
        captureWarning(
            `Booking guidance session type '${bookingGuidanceSessionType}' is a guidance onboarding session type after the roadmap is complete`,
        );
        return <Redirect href={validDoneRoute} />;
    }

    const bookingLink = getGuideBookingLinkUrl(
        bookingGuidanceSessionType,
        guide?.guideBookingLinks,
    );

    if (bookingLink == null) {
        captureException(new Error(`Could not find booking link for Guide.`), {
            bookingGuidanceSessionType,
        });
        return <Redirect href={validDoneRoute} />;
    }

    const onEventScheduled = () => {
        setShouldShowDoneButton(true);

        void afterBookingGuidanceSession(bookingGuidanceSessionType);

        if (doneRoute === GUIDANCE_TAB_ROUTE) {
            setHasRecentlyBookedSession(true);
        }
    };

    const onPressDone = () => {
        if (isWaiting) return;
        setIsWaiting(true);
        setTimeout(async () => {
            await queryClient.invalidateQueries({
                queryKey: ['searchGuidanceSessions'],
            });
            // book-session is a modal on iOS — clear the stack before replacing
            if (Platform.OS === 'ios' && router.canDismiss()) {
                router.dismissAll();
                setTimeout(() => {
                    router.replace(validDoneRoute);
                }, 50);
            } else {
                router.replace(validDoneRoute);
            }
            setIsWaiting(false);
        }, 2000); // Wait for webhook to settle
    };

    trackViewOnce('GuidanceSessions/BookSession');

    const headerCopyHeight =
        HEADER_COPY_BASE_HEIGHT + (isNonMobileWeb ? 20 : 0);
    const doneButtonHeight = shouldShowDoneButton
        ? DONE_BUTTON_BASE_HEIGHT + insets.bottom
        : 0;
    const calendlyContainerHeight =
        height -
        (insets.top + insets.bottom) -
        (HEADER_SIDE_HEIGHT + headerCopyHeight + doneButtonHeight) -
        (isWeb ? 40 : 20); // extra buffer for web (needed for mobile web)

    return (
        <View style={{ flex: 1 }}>
            <YStack
                marginHorizontal={20}
                marginTop={16}
                flex={1}
                $platform-web={{
                    marginHorizontal: 0,
                    overflow: 'hidden',
                }}
            >
                <XStack
                    justifyContent="space-between"
                    alignItems="center"
                    paddingHorizontal={isWeb ? 12 : 0}
                >
                    <YStack
                        width={HEADER_SIDE_WIDTH}
                        height={HEADER_SIDE_HEIGHT}
                        alignItems="flex-start"
                        justifyContent="center"
                    >
                        {!shouldShowSimpleHeader && (
                            <BackButton
                                fallbackRoute={validDoneRoute}
                                customBackFn={() => {
                                    router.replace(validDoneRoute);
                                }}
                                background="gray"
                                iconType={BackButtonIconType.ARROW_LEFT}
                                onBeforeGoingBack={() => {
                                    track(AnalyticsUserEvent.BACK, {
                                        pathname:
                                            '/guidance-sessions/book-session',
                                        flow: 'GuidanceSessions',
                                    });
                                }}
                            />
                        )}
                    </YStack>
                    <BodySmallRegular>New session</BodySmallRegular>
                    <XStack
                        width={HEADER_SIDE_WIDTH}
                        height={HEADER_SIDE_HEIGHT}
                    ></XStack>
                </XStack>
                <YStack
                    alignItems="center"
                    justifyContent="center"
                    height={headerCopyHeight}
                >
                    <H6Regular>
                        {shouldShowSimpleHeader
                            ? '' // Leaving the elements here but empty to keep layout the same
                            : 'Find a date that works for you'}
                    </H6Regular>
                </YStack>

                <InlineCalendlyWidget
                    calendlyUrl={bookingLink}
                    onEventScheduled={onEventScheduled}
                    // we need to tell the inline widget how much space it has available
                    // take the screen height and subtract the vertical safe area insets and the heights of all elements
                    containerHeight={calendlyContainerHeight}
                />
                {shouldShowDoneButton && (
                    <View
                        flexShrink={1}
                        height={doneButtonHeight}
                        width="100%"
                        maxWidth={600}
                        alignSelf="center"
                        paddingHorizontal={isWeb ? 16 : 0}
                    >
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            onPress={onPressDone}
                            text={isWaiting ? '' : 'Done'}
                            icon={isWaiting ? <Spinner /> : null}
                            disabled={isWaiting}
                        />
                    </View>
                )}
            </YStack>
        </View>
    );
};

export default BookSession;
